import { createElement, useContext, useMemo } from "react";
import {
  PortableText,
  type PortableTextBlockComponent,
  type PortableTextComponents,
  type PortableTextListComponent,
  type PortableTextListItemComponent,
  type PortableTextMarkComponent,
  type PortableTextTypeComponentProps,
} from "@portabletext/react";
import VideoEmbed from "../Video/Video.jsx";
import Button from "../Button/Button.jsx";
import { ModuleTime } from "../ModuleTime/ModuleTime.jsx";
import type { SanityBlockContent } from "../../../api/spark";
import { blockContentStyles as styles } from "./blockContent.css";
import GalleryBlock from "./components/GalleryBlock/GalleryBlock";
import CustomComponentsCtx from "./CustomComponentsCtx";
import Branch from "./components/BranchBlock/BranchBlock";
import GeniallyIframe from "./components/GeniallyIframe/GeniallyIframe";
import ImageBlock from "./components/ImageBlock/ImageBlock";
import GlossaryEntry from "./components/GlossaryEntry/GlossaryEntry";
import { GLOSSARY_MARK_TYPE } from "./glossary";
import { SparkChatBlock } from "../../../features/workshops/chat-bot/SparkChatBlock";

type TextColor = "dark" | "white";

const glossaryMark: PortableTextMarkComponent<{
  _type: string;
  term: string;
}> = ({ value, children }) => (
  <GlossaryEntry term={value?.term ?? ""}>{children}</GlossaryEntry>
);

const Quote = ({
  value,
}: PortableTextTypeComponentProps<{
  author?: string;
  content: SanityBlockContent;
  source?: string;
}>) => {
  const customComponents = useContext(CustomComponentsCtx)?.customComponents;
  const { author, content, source } = value;

  let Credit = <>{author}</>;
  if (source) {
    Credit = (
      <a href={source} target="_blank" rel="noreferrer">
        {author}
      </a>
    );
  }

  return (
    <div className={styles.quote}>
      <div className={styles.quoteContent}>
        <PortableText value={content} components={customComponents} />
      </div>
      {author && <div className={styles.credit}>{Credit}</div>}
    </div>
  );
};

const TimeBlock = ({
  value,
}: PortableTextTypeComponentProps<{ minutes: number }>) => {
  const { minutes } = value;
  return (
    <span className={styles.duration}>
      <ModuleTime duration={minutes} />
    </span>
  );
};

const GoToButton = ({
  value,
}: PortableTextTypeComponentProps<{ value: string }>) => {
  const target = value.value;
  if (target !== "library" && target !== "showroom") {
    return <></>;
  }
  const isLibrary = target === "library";

  const link = isLibrary ? "../library" : "../showroom";
  const label = isLibrary ? "Bibliothek" : "Showroom";

  return (
    <Button
      to={link}
      width={"fit-content"}
      iconRight={isLibrary ? "library" : "showroom"}
    >
      {label}
    </Button>
  );
};

const MyDownloadButton = ({
  value,
}: PortableTextTypeComponentProps<{
  file: { asset?: { _ref: string }; label?: string };
}>) => {
  const { asset, label } = value.file;
  if (!asset)
    return <p className={styles.error}>Download button misses file!</p>;
  const [, name, ext] = asset._ref.split("-");
  const link = `https://cdn.sanity.io/files/${
    import.meta.env.VITE_SANITY_PROJECT_ID
  }/production/${name}.${ext}?dl=`;
  return (
    <Button onClick={() => window.open(link, "_blank")}>
      {label || "Download"}
    </Button>
  );
};

// Shared inline/list serializers, reused by the top-level renderer and StyledBox.
const linkMark: PortableTextMarkComponent<{ _type: string; href: string }> = ({
  children,
  value,
}) => (
  <a
    className={styles.link}
    href={value?.href}
    target="_blank"
    rel="noreferrer"
  >
    {children}
  </a>
);

const listComponents: Record<string, PortableTextListComponent> = {
  bullet: ({ children }) => <ul className={styles.ul}>{children}</ul>,
  number: ({ children }) => <ol className={styles.ol}>{children}</ol>,
};

const listItemComponents: Record<string, PortableTextListItemComponent> = {
  bullet: ({ children }) => <li className={styles.ulLi}>{children}</li>,
  number: ({ children }) => <li className={styles.olLi}>{children}</li>,
};

type BlockType = NonNullable<
  NonNullable<Parameters<typeof styles.block>[0]>["type"]
>;

// Sanity styles not covered by the `block` recipe (e.g. h1, blockquote) are
// passed through as-is, as before; the recipe then simply adds no variant class.
const toBlockType = (style: string | undefined) =>
  (style || "normal") as BlockType;

// Components used inside a StyledBox: blocks inherit the box's own text color
// (no textColor variant) so headings/paragraphs/lists read against the box bg.
const boxBlock: PortableTextBlockComponent = ({ value, children }) => {
  const el = value.style === "h2" || value.style === "h3" ? value.style : "p";
  return createElement(
    el,
    { className: styles.block({ type: toBlockType(value.style) }) },
    children,
  );
};

const boxComponents: Partial<PortableTextComponents> = {
  block: boxBlock,
  marks: { link: linkMark, [GLOSSARY_MARK_TYPE]: glossaryMark },
  list: listComponents,
  listItem: listItemComponents,
};

const styledBoxTypes = ["goal", "task", "summary", "learnMore"] as const;
type StyledBoxType = (typeof styledBoxTypes)[number];

const isStyledBoxType = (style: string | undefined): style is StyledBoxType =>
  styledBoxTypes.includes(style as StyledBoxType);

const StyledBox = ({
  value,
}: PortableTextTypeComponentProps<{
  content: SanityBlockContent;
  style?: string;
}>) => {
  const { content, style } = value;
  const type = isStyledBoxType(style) ? style : "goal";
  return (
    <div className={styles.styledBox({ type })}>
      <PortableText value={content} components={boxComponents} />
    </div>
  );
};

const getCustomComponents = ({
  textColor = "dark",
}: {
  textColor?: TextColor;
}): Partial<PortableTextComponents> => ({
  block: ({ value, children }) => {
    const el = value.style === "h2" || value.style === "h3" ? value.style : "p";
    const isNormal = ["normal", "h2", "h3", undefined].includes(value.style);
    const className = styles.block({
      type: toBlockType(value.style),
      textColor: isNormal ? textColor : undefined,
    });
    return createElement(el, { className }, children);
  },
  types: {
    image: ImageBlock,
    complexImage: ImageBlock,
    video: ({ value }: PortableTextTypeComponentProps<{ url: string }>) => (
      <VideoEmbed url={value.url} />
    ),
    quote: Quote,
    branch: Branch,
    timeBlock: TimeBlock,
    downloadButton: MyDownloadButton,
    gallery: GalleryBlock,
    genially: ({
      value,
    }: PortableTextTypeComponentProps<{ url: string; title?: string }>) => (
      <GeniallyIframe url={value.url} title={value.title} />
    ),
    goToButton: GoToButton,
    styledBox: StyledBox,
    chatBlock: SparkChatBlock,
  },
  marks: {
    link: linkMark,
    [GLOSSARY_MARK_TYPE]: glossaryMark,
  },
  list: listComponents,
  listItem: listItemComponents,
});

type BlockContentProps = {
  content: SanityBlockContent;
  textColor?: TextColor;
};

/**
 * Renders Spark Content from Sanity.
 */
const BlockContent = ({ content, textColor = "dark" }: BlockContentProps) => {
  const ctxVal = useMemo(
    () => ({
      customComponents: getCustomComponents({ textColor }),
    }),
    [textColor],
  );
  return (
    <CustomComponentsCtx.Provider value={ctxVal}>
      <PortableText value={content} components={ctxVal.customComponents} />
    </CustomComponentsCtx.Provider>
  );
};

export default BlockContent;
